import React, { useState, useEffect } from "react"
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Pagination,
  Paper,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
} from "@mui/material"
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
  timelineOppositeContentClasses,
} from "@mui/lab"
import { useNavigate } from "react-router-dom"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import type { VimeoVideo } from "../../services/vimeo.service"
import vimeoService from "../../services/vimeo.service"

const MuiTimelineVideoFeed: React.FC = () => {
  const [videos, setVideos] = useState<VimeoVideo[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const fetchVideos = async (pageNumber: number) => {
    try {
      setLoading(true)
      const response = await vimeoService.getVideos(pageNumber)
      setVideos(response.data)

      // Calculate total pages based on paging info
      if (response.paging.next) {
        const nextUrl = new URL(response.paging.next)
        const nextPage = parseInt(nextUrl.searchParams.get("page") || "0", 10)
        setTotalPages(nextPage > 0 ? nextPage : 1)
      }

      setLoading(false)
    } catch (err) {
      setError("Failed to load videos. Please try again later.")
      setLoading(false)
      console.error("Error fetching videos:", err)
    }
  }

  useEffect(() => {
    fetchVideos(page)
  }, [page])

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
    window.scrollTo(0, 0)
  }

  const handleVideoClick = (videoId: string) => {
    navigate(`/video/player/${videoId}`)
  }

  // Format duration from seconds to mm:ss
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.floor(seconds % 60)
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`
  }

  // Format date to more readable format
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  // Get the appropriate thumbnail for each video
  const getThumbnail = (video: VimeoVideo): string => {
    if (!video.pictures || !video.pictures.sizes || video.pictures.sizes.length === 0) {
      return "" // Default or placeholder image could be used here
    }

    // Find a medium-sized thumbnail
    const mediumThumbnail = video.pictures.sizes.find((size) => size.width >= 640)
    return mediumThumbnail?.link || video.pictures.sizes[video.pictures.sizes.length - 1].link
  }

  if (loading && videos.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    )
  }

  if (error && videos.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Collect Tutorial Videos
      </Typography>

      <Timeline
        position={"right"}
        sx={{
          p: 0,
          mt: 4,
          [`& .${timelineOppositeContentClasses.root}`]: {
            flex: 0.2,
          },
        }}>
        {videos.map((video) => {
          // Extract video ID from URI (format: "/videos/123456789")
          const videoId = video.uri.split("/").pop() || ""

          return (
            <TimelineItem key={video.uri}>
              {!isMobile && (
                <TimelineOppositeContent sx={{ m: "auto 0" }} color="text.secondary">
                  <Typography variant="body2" component="span">
                    {formatDate(video.created_time)}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      icon={<AccessTimeIcon />}
                      label={formatDuration(video.duration)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </TimelineOppositeContent>
              )}

              <TimelineSeparator>
                <TimelineDot color="primary" variant="outlined">
                  <VideoLibraryIcon />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>

              <TimelineContent sx={{ py: 2, px: 2 }}>
                <Card
                  sx={{
                    width: "100%",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                    },
                  }}>
                  <Box position="relative">
                    <CardMedia
                      component="img"
                      height={isMobile ? 160 : 200}
                      image={getThumbnail(video)}
                      alt={video.name}
                    />
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        bgcolor: "rgba(0, 0, 0, 0.5)",
                        color: "white",
                        "&:hover": {
                          bgcolor: "rgba(0, 0, 0, 0.7)",
                        },
                      }}
                      onClick={() => handleVideoClick(videoId)}>
                      <PlayArrowIcon fontSize="large" />
                    </IconButton>
                  </Box>

                  <CardContent>
                    <Typography variant="h6" component="div">
                      {video.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 1,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                      {video.description}
                    </Typography>

                    {isMobile && (
                      <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Chip
                          icon={<CalendarTodayIcon />}
                          label={formatDate(video.created_time)}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          icon={<AccessTimeIcon />}
                          label={formatDuration(video.duration)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </CardContent>

                  <CardActions>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => handleVideoClick(videoId)}
                      startIcon={<PlayArrowIcon />}>
                      Watch Video
                    </Button>
                  </CardActions>
                </Card>
              </TimelineContent>
            </TimelineItem>
          )
        })}
      </Timeline>

      {videos.length > 0 && (
        <Box display="flex" justifyContent="center" mt={4} mb={2}>
          <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
        </Box>
      )}
    </Container>
  )
}

export default MuiTimelineVideoFeed
